"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useDelegationStore } from "@/lib/delegation-store";
import {
  useConditions,
  useCreateCondition,
  useUpdateCondition,
  useDeleteCondition,
} from "@/hooks/use-conditions";
import { useSuggestions } from "@/hooks/use-suggestions";
import { Button } from "@/components/ui/button";
import { CalendarDatePicker } from "@/components/ui/calendar-date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import type { Condition, ConditionStatus } from "@/types/condition";

const STATUS_BADGE: Record<
  ConditionStatus,
  { key: string; variant: "default" | "secondary" | "outline" }
> = {
  active: { key: "statusActive", variant: "default" },
  resolved: { key: "statusResolved", variant: "secondary" },
  managed: { key: "statusManaged", variant: "outline" },
};

function normalizeConditionStatus(status: string | null | undefined): ConditionStatus {
  return status === "resolved" || status === "managed" ? status : "active";
}

// ─── Add Condition Form ───────────────────────────────────────

function AddConditionForm() {
  const t = useTranslations("conditions");
  const tc = useTranslations("common");
  const [name, setName] = useState("");
  const [diseaseId, setDiseaseId] = useState<string | null>(null);
  const [debouncedName, setDebouncedName] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: suggestionsData } = useSuggestions(debouncedName, "condition");
  const suggestions = suggestionsData?.data ?? [];
  const createCondition = useCreateCondition();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(name), 300);
    return () => clearTimeout(timer);
  }, [name]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createCondition.mutate(
      {
        name: name.trim(),
        diseaseIdentifier: diseaseId ?? undefined,
        status: "active",
      },
      {
        onSuccess: () => {
          setName("");
          setDiseaseId(null);
        },
      }
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-3">
      <div className="relative flex-1" ref={wrapperRef}>
        <Label htmlFor="condition-name" className="mb-2 block">
          {t("conditionName")}
        </Label>
        <Input
          id="condition-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setDiseaseId(null);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder={t("conditionPlaceholder")}
          autoComplete="off"
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border bg-popover p-1 shadow-md">
            {suggestions.map((s, i) => (
              <li key={`${s.canonicalQuery}-${i}`}>
                <button
                  type="button"
                  className="flex w-full flex-col rounded-md px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    setName(s.label);
                    setDiseaseId(s.canonicalQuery);
                    setShowSuggestions(false);
                  }}
                >
                  <span className="font-medium">{s.label}</span>
                  {s.secondaryLabel && (
                    <span className="text-xs text-muted-foreground">
                      {s.secondaryLabel}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Button type="submit" disabled={!name.trim() || createCondition.isPending}>
        {createCondition.isPending ? tc("adding") : tc("add")}
      </Button>
    </form>
  );
}

// ─── Edit Dialog ──────────────────────────────────────────────

function EditDialog({
  condition,
  open,
  onOpenChange,
}: {
  condition: Condition;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useTranslations("conditions");
  const tc = useTranslations("common");
  const [name, setName] = useState(condition.name);
  const [status, setStatus] = useState<ConditionStatus>(
    normalizeConditionStatus(condition.status)
  );
  const [notes, setNotes] = useState(condition.notes ?? "");
  const [diagnosedAt, setDiagnosedAt] = useState(
    condition.diagnosedAt ?? ""
  );
  const updateCondition = useUpdateCondition();

  function handleSave() {
    updateCondition.mutate(
      {
        id: condition.id,
        data: {
          name,
          status,
          notes: notes || undefined,
          diagnosedAt: diagnosedAt ? `${diagnosedAt}T00:00:00Z` : undefined,
        },
      },
      { onSuccess: () => onOpenChange(false) }
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("editCondition")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">{tc("name")}</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>{tc("status")}</Label>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as ConditionStatus)}
            >
              <SelectTrigger className="w-full">
                {/* Render the localized label explicitly: Base UI's SelectValue
                    falls back to the raw value until the popup mounts its items. */}
                <SelectValue>
                  {t(`status${status.charAt(0).toUpperCase()}${status.slice(1)}`)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t("statusActive")}</SelectItem>
                <SelectItem value="resolved">{t("statusResolved")}</SelectItem>
                <SelectItem value="managed">{t("statusManaged")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("diagnosed")}</Label>
            <CalendarDatePicker
              value={diagnosedAt || undefined}
              onChange={setDiagnosedAt}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-notes">{tc("notes")}</Label>
            <Textarea
              id="edit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("clinicalNotes")}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>
            {tc("cancel")}
          </DialogClose>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || updateCondition.isPending}
          >
            {updateCondition.isPending ? tc("saving") : tc("save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Delete Dialog ────────────────────────────────────────────

function DeleteDialog({
  condition,
  open,
  onOpenChange,
}: {
  condition: Condition;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useTranslations("conditions");
  const tc = useTranslations("common");
  const deleteCondition = useDeleteCondition();

  function handleConfirm() {
    deleteCondition.mutate(condition.id, {
      onSuccess: () => onOpenChange(false),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("deleteCondition")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t("deleteConfirm", { name: condition.name })}{" "}
          {tc("cannotBeUndone")}
        </p>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>
            {tc("cancel")}
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={deleteCondition.isPending}
          >
            {deleteCondition.isPending ? tc("deleting") : tc("delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Condition Row ────────────────────────────────────────────

function ConditionRow({ condition }: { condition: Condition }) {
  const t = useTranslations("conditions");
  const tc = useTranslations("common");
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const status = normalizeConditionStatus(condition.status);
  const badge = STATUS_BADGE[status];

  return (
    <>
      <div className="space-y-2 rounded-lg border p-4">
        <p className="font-medium">{condition.name}</p>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant={badge.variant}>{t(badge.key)}</Badge>
        </div>
        {condition.diagnosedAt && (
          <p className="text-xs text-muted-foreground">
            {t("diagnosed")}: {condition.diagnosedAt}
          </p>
        )}
        {condition.notes && (
          <p className="line-clamp-2 text-sm text-muted-foreground">
            {condition.notes}
          </p>
        )}
        <div className="flex justify-end gap-1 pt-1">
          <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
            {tc("edit")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setDeleteOpen(true)}
          >
            {tc("delete")}
          </Button>
        </div>
      </div>
      <EditDialog
        condition={condition}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
      <DeleteDialog
        condition={condition}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
      />
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────

export default function ConditionsPage() {
  const t = useTranslations("conditions");
  const onBehalfOfUserId = useDelegationStore((s) => s.onBehalfOfUserId);
  const { data: conditions, isLoading } = useConditions();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-full" />
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold tracking-tight">
        {t(onBehalfOfUserId ? "title" : "myTitle")}
      </h1>

      <AddConditionForm />

      {conditions && conditions.length > 0 ? (
        <div className="space-y-3">
          {conditions.map((c) => (
            <ConditionRow key={c.id} condition={c} />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-sm text-muted-foreground">
            {t("emptyState")}
          </p>
        </div>
      )}
    </div>
  );
}
